import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Chip, ListItem, ListItemIcon, Popper, TextField } from '@mui/material';
import { StyledFlex } from '@shared/styles/styled';
import { PLACEHOLDER_FADE_DURATION_MS } from '@components/AppLayout/Topbar/CopilotSearch/constants/constants';

/**
 * Defines CSS custom properties for the Copilot search component's glow effect.
 * These properties are used for the animated gradient border.
 * @param {object} theme - The application's theme object.
 * @returns {import('@emotion/react').SerializedStyles} The CSS styles.
 */
const copilotSearchStyles = (theme) => css`
  @property --glow-deg {
    syntax: '<angle>';
    inherits: true;
    initial-value: 90.22deg;
  }

  @property --blue {
    syntax: '<color>';
    inherits: false;
    initial-value: ${theme.copilot.blue};
  }

  @property --purple {
    syntax: '<color>';
    inherits: false;
    initial-value: ${theme.copilot.purple};
  }

  @property --orange {
    syntax: '<color>';
    inherits: false;
    initial-value: ${theme.copilot.orange};
  }
`;

/**
 * Keyframes for the rotating glow animation on the search input border.
 */
const glow = keyframes`
  0% {
    --glow-deg: -90.22deg;
  }
  100% {
    --glow-deg: 270.22deg;
  }
`;

/**
 * A styled container for the entire Copilot search component.
 * It handles positioning and sizing, including the modal-like overlay effect
 * when the search is active on smaller screens.
 */
export const StyledCopilotContainer = styled(StyledFlex, {
  shouldForwardProp: (prop) => !['open', 'matches'].includes(prop),
})(({ open, matches }) => ({
  minWidth: '200px',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '8px',

  ...(open && {
    position: !matches ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    padding: !matches ? '18px 162px' : '18px clamp(0.5vw, 162px, 10rem)',
    zIndex: 5001,
  }),
}));

/**
 * A styled wrapper for the search input that creates the animated gradient border effect.
 * It uses a combination of background gradients and pseudo-elements to achieve the look.
 */
export const StyledCopilotSearch = styled('div')`
  ${({ theme }) => copilotSearchStyles(theme)}

  position: relative;
  border-radius: 6px;
  border: 3px solid transparent;
  background:
    linear-gradient(canvas 0 0) padding-box,
    linear-gradient(var(--glow-deg, 90.22deg), var(--blue) 0.03%, var(--purple) 51.97%, var(--orange) 100.04%)
      border-box;
  max-height: 40px;
  isolation: isolate;

  &:focus-within {
    background:
      linear-gradient(canvas 0 0) padding-box,
      conic-gradient(
          from var(--glow-deg, 90.22deg),
          var(--blue),
          var(--purple),
          var(--orange),
          var(--purple),
          var(--blue)
        )
        border-box;
    animation: ${glow} 20s infinite linear;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: inherit;
    pointer-events: none;
  }

  // inside of box - white background
  &::before {
    background: ${({ theme }) => `${theme.colors.white} 0 0`};
    opacity: 1;
    z-index: -1;
    inset: 0rem;
  }

  /* Outer glow - to mimic box-shadow*/
  & .Mui-focused {
    &::after {
      background: conic-gradient(
        from var(--glow-deg, 90.22deg),
        var(--blue),
        var(--purple),
        var(--orange),
        var(--purple),
        var(--blue)
      );

      animation: ${glow} 20s infinite linear;
      inset: -1px;
      z-index: -2;
      filter: blur(5px);
      opacity: 1;
    }
  }
`;

/**
 * A styled Popper component for the Autocomplete dropdown.
 * It customizes the appearance of the dropdown paper and listbox.
 */
export const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiAutocomplete-listbox': {
    padding: 0,
    maxHeight: '438px',
    backgroundColor: theme.colors.white,
  },

  '& .MuiAutocomplete-paper': {
    marginTop: '11px',
    backgroundColor: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '15px',
    paddingBottom: '5px',
    height: '100%',
    boxShadow: theme.boxShadows.warningHeader,
    borderRadius: '10px',
    overflow: 'clip',
  },
}));

/**
 * A styled Chip component used for displaying the selected "Suggested Prompt" tag.
 */
export const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.copilot.selectedPromptBg,
  color: theme.colors.white,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '14px',
  padding: '6px 9px',
  borderRadius: '5px',
  height: 'auto',

  '& .MuiChip-deleteIcon': {
    display: 'none',
  },
}));

/**
 * A styled TextField component for the Copilot search input.
 * It includes styles for the input text, placeholder, and placeholder animation.
 */
export const StyledCopilotTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: theme.colors.primary,
    fontFamily: 'Montserrat',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: 0,
    lineHeight: '22px',
    verticalAlign: 'middle',
    width: '100%',

    minWidth: '250px',
    padding: '0px 15px 0px 5px',

    '&::placeholder': {
      color: theme.copilot.placeholder,
      fontFamily: 'Montserrat',
      fontSize: '16px',
      fontWeight: 400,
      letterSpacing: 0,
      lineHeight: '22px',
      verticalAlign: 'middle',
      transition: `opacity ${PLACEHOLDER_FADE_DURATION_MS}ms ease-in-out`,
    },

    '& input[data-placeholder-visible="true"]::placeholder': {
      opacity: 1,
    },
    '& input[data-placeholder-visible="false"]::placeholder': {
      opacity: 0,
    },
  },

  '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottom: 'none',
  },

  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    marginRight: '7px',
    borderRadius: '3px',

    '& .MuiAutocomplete-clearIndicator': {
      borderRadius: '3px',
      padding: '3px',

      '&:hover': {
        backgroundColor: theme.colors.lightGray2,
      },

      '& .MuiSvgIcon-root': {
        fontSize: '24px',
        color: theme.colors.primary,
      },
    },
  },
}));

/**
 * A styled ListItem component for rendering options in the Autocomplete dropdown.
 * It applies different styles based on whether the option is a "Suggested Question".
 */
export const StyledCopilotListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'isSuggestedQuestions',
})(({ theme, isSuggestedQuestions }) => ({
  width: isSuggestedQuestions ? 'auto' : '100%',

  '&.MuiAutocomplete-option': {
    padding: '7px 10px',
    backgroundColor: isSuggestedQuestions ? theme.colors.bgColorOptionTwo : 'transparent',
    borderRadius: isSuggestedQuestions ? '6px' : '0',
    alignSelf: 'flex-start',

    '&.Mui-focused': {
      backgroundColor: isSuggestedQuestions ? theme.copilot.questionHoverBg : theme.colors.bgColorOptionTwo,
    },

    '&:hover': {
      backgroundColor: isSuggestedQuestions ? theme.copilot.questionHoverBg : theme.colors.bgColorOptionTwo,
    },

    '& .MuiListItemIcon-root': {
      color: theme.colors.primary,
      minWidth: '28px',
    },
  },
}));

/**
 * A styled Chip component used to display "Suggested Questions" options.
 * It's designed to look like a chip but functions as a regular option.
 */
export const StyledCopilotQuestionChip = styled(Chip)(({ theme }) => ({
  borderRadius: '6px',
  backgroundColor: 'transparent',
  color: theme.colors.primary,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '14px',
  fontFamily: 'Montserrat',
  overflow: 'visible',
  width: '100%',
  height: 'auto',

  '& .MuiChip-label': {
    padding: 0,
  },
}));

/**
 * A styled ListItemIcon for use within the Copilot search options.
 */
export const StyledCopilotListItemIcon = styled(ListItemIcon)(() => ({
  fontSize: '22px',
}));

/**
 * A styled container for the '/' symbol in the search tooltip.
 * It creates a small bordered box around the slash.
 */
export const StyledCopilotSlashContainer = styled(StyledFlex)(({ theme }) => ({
  display: 'inline-flex',
  color: 'inherit',
  border: `1px solid ${theme.colors.white}`,
  padding: '2px 8px',
  borderRadius: '2px',
  fontSize: 'inherit',
  fontWeight: 500,
  marginLeft: '4px',
}));